/**
 * CalculatorForm — type-specific fields for a "calculator" service
 * (KAN-157 split out of CreateServiceDialog.tsx).
 *
 * Renders the required_inputs table, the constants table, and the formula
 * editor. All state lives in the parent dialog; this component is a pure
 * render of that slice plus the setters it needs.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { FormulaEditor } from '@/components/services/FormulaEditor';
import type { FormulaVariable } from '@/components/services/FormulaEditor';
import type { ConstantRow, RequiredInputRow } from './types';

interface CalculatorFormProps {
  requiredInputs: RequiredInputRow[];
  constants: ConstantRow[];
  formula: string;
  onFormulaChange: (value: string) => void;
  onAddRequiredInputRow: () => void;
  onRemoveRequiredInputRow: (index: number) => void;
  onUpdateRequiredInput: (index: number, field: keyof RequiredInputRow, value: string) => void;
  onAddConstantRow: () => void;
  onRemoveConstantRow: (index: number) => void;
  onUpdateConstant: (index: number, field: keyof ConstantRow, value: string) => void;
}

export function CalculatorForm({
  requiredInputs,
  constants,
  formula,
  onFormulaChange,
  onAddRequiredInputRow,
  onRemoveRequiredInputRow,
  onUpdateRequiredInput,
  onAddConstantRow,
  onRemoveConstantRow,
  onUpdateConstant,
}: CalculatorFormProps): JSX.Element {
  return (
    <>
      {/* Required inputs table */}
      <div className="space-y-2">
        <Label>Входные параметры</Label>
        <div data-testid="table-required-inputs">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-2 font-medium">Имя параметра</th>
                <th className="py-2 pr-2 font-medium">Ед. измерения</th>
                <th className="py-2 pr-2 font-medium">Тип</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {requiredInputs.map((row, index) => (
                <tr
                  key={index}
                  data-testid={`required-input-row-${index}`}
                  className="border-b"
                >
                  <td className="py-1.5 pr-2">
                    <Input
                      value={row.name}
                      onChange={(e) => onUpdateRequiredInput(index, 'name', e.target.value)}
                      placeholder="Имя"
                      className="h-8"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      value={row.unit}
                      onChange={(e) => onUpdateRequiredInput(index, 'unit', e.target.value)}
                      placeholder="Ед."
                      className="h-8"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      value={row.type}
                      onChange={(e) => onUpdateRequiredInput(index, 'type', e.target.value)}
                      placeholder="Тип"
                      className="h-8"
                    />
                  </td>
                  <td className="py-1.5">
                    <button
                      data-testid="remove-row"
                      onClick={() => onRemoveRequiredInputRow(index)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          data-testid="add-required-input-row"
          variant="outline"
          size="sm"
          onClick={onAddRequiredInputRow}
          type="button"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Добавить параметр
        </Button>
      </div>

      {/* Constants table */}
      <div className="space-y-2">
        <Label>Константы</Label>
        <div data-testid="table-constants">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-2 font-medium">Имя</th>
                <th className="py-2 pr-2 font-medium">Значение</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {constants.map((row, index) => (
                <tr key={index} data-testid={`constant-row-${index}`} className="border-b">
                  <td className="py-1.5 pr-2">
                    <Input
                      value={row.name}
                      onChange={(e) => onUpdateConstant(index, 'name', e.target.value)}
                      placeholder="Имя"
                      className="h-8"
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input
                      value={row.value}
                      onChange={(e) => onUpdateConstant(index, 'value', e.target.value)}
                      placeholder="Значение"
                      className="h-8"
                      type="number"
                    />
                  </td>
                  <td className="py-1.5">
                    <button
                      data-testid="remove-row"
                      onClick={() => onRemoveConstantRow(index)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          data-testid="add-constant-row"
          variant="outline"
          size="sm"
          onClick={onAddConstantRow}
          type="button"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Добавить константу
        </Button>
      </div>

      {/* Formula editor */}
      <FormulaEditor
        value={formula}
        onChange={onFormulaChange}
        variables={[
          ...requiredInputs
            .filter((r) => r.name.trim())
            .map((r): FormulaVariable => ({
              name: r.name,
              label: r.name,
              unit: r.unit || undefined,
              source: 'input',
            })),
          ...constants
            .filter((c) => c.name.trim())
            .map((c): FormulaVariable => ({
              name: c.name,
              label: c.name,
              source: 'input',
            })),
        ]}
        mode="calculator"
      />
    </>
  );
}
